import React, { useState } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { motion, AnimatePresence } from "framer-motion";
import { trackEvent } from "../lib/analytics";
import { CheckCircle, Calendar, Phone, MapPin, MessageSquare, ChevronLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const CATEGORIES = ["Кашемировые пальто", "Пуховики", "Шубы", "Пальто с мехом", "Құдалық-сеты", "Ателье", "Перешив и реставрация"];

export default function Appointment() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [form, setForm] = useState({
    name: user?.name || "", phone: user?.phone || "", city: user?.city || "",
    category: "", date: "", comment: ""
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) { setError(t.appointment.errorRequired); return; }
    setError(""); setLoading(true);
    try {
      await axios.post(`${API}/appointments`, form);
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.detail || t.appointment.errorSend);
    } finally { setLoading(false); }
  };

  const inputStyle = { width: "100%", background: "var(--ang-surface)", border: "1px solid var(--ang-border)", borderRadius: "4px", padding: "14px 14px 14px 44px", color: "var(--ang-text)", fontFamily: "Manrope", fontSize: "15px", outline: "none" };

  return (
    <div style={{ background: "var(--ang-bg)", minHeight: "100vh" }}>
      <div style={{ padding: "52px 20px 20px", display: "flex", alignItems: "center", gap: "12px" }}>
        <button onClick={() => navigate(-1)} style={{ background: "none", border: "none", color: "var(--ang-text-muted)", cursor: "pointer" }}>
          <ChevronLeft size={20} />
        </button>
        <h1 style={{ fontFamily: "Cormorant Garamond", fontSize: "28px", color: "var(--ang-text)", fontWeight: 400, margin: 0 }}>{t.appointment.title}</h1>
      </div>

      <AnimatePresence mode="wait">
        {!done ? (
          <motion.form key="form" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} onSubmit={handleSubmit}
            style={{ padding: "0 20px 32px", display: "flex", flexDirection: "column", gap: "14px" }}>
            <p style={{ fontFamily: "Manrope", fontSize: "13px", color: "var(--ang-text-faint)", lineHeight: 1.6, marginBottom: "8px" }}>
              {t.appointment.subtitle}
            </p>

            {[
              { key: "name", Icon: null, placeholder: t.appointment.name, type: "text" },
              { key: "phone", Icon: Phone, placeholder: t.appointment.phone, type: "tel" },
              { key: "city", Icon: MapPin, placeholder: t.appointment.city, type: "text" },
              { key: "date", Icon: Calendar, placeholder: t.appointment.date, type: "text" },
            ].map(({ key, Icon, placeholder, type }) => (
              <div key={key} style={{ position: "relative" }}>
                {Icon && <Icon size={15} style={{ position: "absolute", left: "14px", top: "50%", transform: "translateY(-50%)", color: "var(--ang-gold)" }} />}
                <input data-testid={`appt-${key}`} type={type} value={form[key]} onChange={e => set(key, e.target.value)} placeholder={placeholder}
                  style={{ ...inputStyle, paddingLeft: Icon ? "44px" : "14px" }} />
              </div>
            ))}

            {/* Category select */}
            <select data-testid="appt-category" value={form.category} onChange={e => set("category", e.target.value)}
              style={{ width: "100%", background: "var(--ang-surface)", border: "1px solid var(--ang-border)", borderRadius: "4px", padding: "14px", color: form.category ? "white" : "var(--ang-text-faint)", fontFamily: "Manrope", fontSize: "15px", outline: "none", cursor: "pointer" }}>
              <option value="" style={{ color: "var(--ang-text-faint)" }}>{t.appointment.category}</option>
              {CATEGORIES.map(c => <option key={c} value={c} style={{ background: "var(--ang-surface)", color: "var(--ang-text)" }}>{c}</option>)}
            </select>

            {/* Comment */}
            <div style={{ position: "relative" }}>
              <MessageSquare size={15} style={{ position: "absolute", left: "14px", top: "16px", color: "var(--ang-gold)" }} />
              <textarea data-testid="appt-comment" value={form.comment} onChange={e => set("comment", e.target.value)} placeholder={t.appointment.comment} rows={3}
                style={{ width: "100%", background: "var(--ang-surface)", border: "1px solid var(--ang-border)", borderRadius: "4px", padding: "14px 14px 14px 44px", color: "var(--ang-text)", fontFamily: "Manrope", fontSize: "15px", outline: "none", resize: "none" }} />
            </div>

            {error && <p style={{ color: "#ff6b6b", fontFamily: "Manrope", fontSize: "13px" }}>{error}</p>}

            <button data-testid="appt-submit" type="submit" disabled={loading}
              style={{ background: "linear-gradient(135deg,var(--ang-gold),var(--ang-gold-2))", color: "var(--ang-bg)", padding: "16px", border: "none", borderRadius: "4px", cursor: "pointer", fontFamily: "Manrope", fontWeight: 700, fontSize: "12px", letterSpacing: "0.12em", textTransform: "uppercase", marginTop: "8px" }}>
              {loading ? t.appointment.submitting : t.appointment.submit}
            </button>
          </motion.form>
        ) : (
          <motion.div key="success" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5 }}
            style={{ padding: "32px 20px", display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center" }}>
            <div style={{ width: "80px", height: "80px", borderRadius: "50%", background: "var(--ang-gold-soft)", border: "2px solid var(--ang-gold)", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "24px" }}>
              <CheckCircle size={36} style={{ color: "var(--ang-gold)" }} />
            </div>
            <h2 style={{ fontFamily: "Cormorant Garamond", fontSize: "28px", color: "var(--ang-text)", fontWeight: 400, marginBottom: "12px" }}>{t.appointment.success}</h2>
            <p data-testid="appt-success-msg" style={{ fontFamily: "Manrope", fontSize: "14px", color: "var(--ang-text-muted)", lineHeight: 1.7, maxWidth: "280px", marginBottom: "32px" }}>
              {t.appointment.thankYou}
            </p>
            <button onClick={() => { trackEvent("whatsapp_click", { page: "/app/appointment" }); window.open("https://wa.me/77018017755", "_blank"); }} data-testid="success-whatsapp-btn"
              style={{ background: "var(--ang-surface)", border: "1px solid rgba(37,211,102,0.3)", borderRadius: "6px", padding: "14px 24px", display: "flex", alignItems: "center", gap: "10px", cursor: "pointer", marginBottom: "16px", fontFamily: "Manrope", fontSize: "14px", color: "#25D366", fontWeight: 600 }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="#25D366"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
              {t.appointment.whatsapp}
            </button>
            <button onClick={() => navigate("/app/home")} style={{ background: "none", border: "none", color: "var(--ang-text-faint)", fontFamily: "Manrope", fontSize: "13px", cursor: "pointer" }}>
              {t.appointment.home}
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
